// src/pages/app-banners/app-banners.page.jsx
// Gestion des bannières publicitaires de l'app mobile TIGOON.

import React, { useEffect, useState } from 'react';
import {
  Table, Modal, Form, Input, Upload, Button, InputNumber, Switch, message,
  Breadcrumb, Image, Tag, Space, Popconfirm, Select, Segmented, Empty
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined,
  EyeOutlined, EyeInvisibleOutlined, RightOutlined, PictureOutlined,
  HomeOutlined, ShopOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { appBannerService, motolivraisonService } from '../../common/services';
// Socle de tableau commun au backoffice (en-tête ardoise, libellés FR, pagination)
import {
  LOCALE_TABLE, PAGINATION_TABLE, BTN_ACTION, COULEURS_ACTION, cmpTexte, CellTooltip,
} from '../motolivraison/table.helpers';
import '../motolivraison/css/motolivraison-table.css';
import './app-banners.css';

export default function AppBannersPage() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();
  // Emplacement affiché (onglet) et référentiels pour le ciblage des bannières Boutique
  const [emplacement, setEmplacement] = useState('accueil');
  const [commerces, setCommerces] = useState([]);
  const [categories, setCategories] = useState([]);
  const cibleType = Form.useWatch('cibleType', form);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const res = await appBannerService.getAll();
      setBanners(res?.data?.banners || res?.banners || []);
    } catch (e) {
      message.error("Impossible de charger les bannières");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBanners(); }, []);

  // Chargés une fois : servent à choisir la cible d'une bannière Boutique
  useEffect(() => {
    (async () => {
      try {
        const [c, cat] = await Promise.all([
          motolivraisonService.listCommerces({ status: 'approved', limit: 1000 }),
          motolivraisonService.listCategories(true),
        ]);
        setCommerces(c?.commerces || []);
        setCategories(cat?.categories || []);
      } catch (e) {
        console.error('Erreur chargement des cibles de bannière:', e);
      }
    })();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setFileList([]);
    form.resetFields();
    form.setFieldsValue({ ordre: 0, isActive: true, emplacement, cibleType: 'aucune' });
    setModalOpen(true);
  };

  const openEdit = (banner) => {
    setEditing(banner);
    setFileList([]);
    form.setFieldsValue({
      titre: banner.titre || '',
      description: banner.description || '',
      ordre: banner.ordre || 0,
      emplacement: banner.emplacement || 'accueil',
      cibleType: banner.cibleType || 'aucune',
      cibleId: banner.cibleId || undefined,
      isActive: banner.isActive,
    });
    setModalOpen(true);
  };

  const onFinish = async (values) => {
    try {
      const payload = {
        titre: values.titre || '',
        description: values.description || '',
        ordre: values.ordre || 0,
        isActive: values.isActive !== false,
        emplacement: values.emplacement || 'accueil',
        cibleType: values.cibleType || 'aucune',
        cibleId: values.cibleType && values.cibleType !== 'aucune' ? values.cibleId : '',
        image: fileList[0]?.originFileObj,
      };
      if (editing) {
        if (!payload.image) delete payload.image;
        await appBannerService.update(editing._id, payload);
        message.success("Bannière mise à jour");
      } else {
        if (!payload.image) {
          message.error("Veuillez sélectionner une image");
          return;
        }
        await appBannerService.create(payload);
        message.success("Bannière créée");
      }
      setModalOpen(false);
      fetchBanners();
    } catch (e) {
      message.error(e?.message || "Erreur lors de l'enregistrement");
    }
  };

  const onToggle = async (banner) => {
    try {
      await appBannerService.toggle(banner._id);
      message.success(banner.isActive ? "Désactivée" : "Activée");
      fetchBanners();
    } catch {
      message.error("Action impossible");
    }
  };

  const onDelete = async (banner) => {
    try {
      await appBannerService.delete(banner._id);
      message.success("Supprimée");
      fetchBanners();
    } catch {
      message.error("Suppression impossible");
    }
  };

  // Emplacement d'une bannière : les bannières créées avant l'ajout du champ
  // n'en portent pas — elles restent des bannières d'accueil.
  const emplacementDe = (b) => b.emplacement || 'accueil';
  const nbAccueil = banners.filter(b => emplacementDe(b) === 'accueil').length;
  const nbBoutique = banners.filter(b => emplacementDe(b) === 'boutique').length;

  const columns = [
    {
      title: 'Aperçu',
      dataIndex: 'imageUrl',
      width: 130,
      align: 'center',
      render: (url) => url
        ? <Image width={100} height={56} src={url} style={{ objectFit: 'cover', borderRadius: 6 }} />
        : <PictureOutlined style={{ fontSize: 24, color: '#bfbfbf' }} />,
    },
    {
      title: 'Titre',
      dataIndex: 'titre',
      width: 280,
      ellipsis: { showTitle: false },
      sorter: (a, b) => cmpTexte(a.titre, b.titre),
      render: (t, r) => (
        <CellTooltip label="Bannière" value={t || 'Sans titre'} extra={r.description}>
          <div>
            <div style={{ fontWeight: 600 }}>
              {t || <span style={{ color: '#bfbfbf' }}>(sans titre)</span>}
            </div>
            {r.description && (
              <div style={{
                fontSize: 12, color: '#8c8c8c',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {r.description}
              </div>
            )}
          </div>
        </CellTooltip>
      ),
    },
    {
      title: 'Cible au clic',
      key: 'cible',
      width: 240,
      filters: [
        { text: 'Décorative', value: 'aucune' },
        { text: 'Boutique', value: 'commerce' },
        { text: 'Rayon', value: 'categorie' },
      ],
      onFilter: (v, r) => (r.cibleType || 'aucune') === v,
      render: (_, r) => {
        if (!r.cibleType || r.cibleType === 'aucune') {
          return <span style={{ color: '#bfbfbf' }}>Décorative</span>;
        }
        const nom = r.cibleType === 'commerce'
          ? commerces.find(c => c._id === r.cibleId)?.nom
          : categories.find(c => c._id === r.cibleId)?.nom;
        return (
          <Tag color={r.cibleType === 'commerce' ? 'orange' : 'purple'}>
            {r.cibleType === 'commerce' ? 'Boutique' : 'Rayon'} : {nom || '—'}
          </Tag>
        );
      },
    },
    {
      title: 'Ordre',
      dataIndex: 'ordre',
      width: 90,
      align: 'center',
      sorter: (a, b) => (a.ordre || 0) - (b.ordre || 0),
      render: (o) => <span style={{ fontWeight: 600 }}>{o ?? 0}</span>,
    },
    {
      title: 'Statut',
      dataIndex: 'isActive',
      width: 120,
      align: 'center',
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (v, r) => Boolean(r.isActive) === v,
      render: (isActive, r) => (
        <Switch
          checked={isActive}
          onChange={() => onToggle(r)}
          checkedChildren="Active"
          unCheckedChildren="Off"
        />
      ),
    },
    {
      title: 'Créée',
      dataIndex: 'createdAt',
      width: 160,
      sorter: (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
      defaultSortOrder: 'descend',
      render: (d) => d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 230,
      align: 'center',
      fixed: 'right',
      render: (_, r) => (
        <Space size={6}>
          <Button size="small" icon={<EditOutlined />} style={BTN_ACTION(COULEURS_ACTION.modifier)}
            onClick={() => openEdit(r)}>
            Modifier
          </Button>
          <Popconfirm
            title="Supprimer cette bannière ?"
            description="Elle disparaîtra immédiatement de l'application."
            onConfirm={() => onDelete(r)}
            okText="Supprimer"
            cancelText="Annuler"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" icon={<DeleteOutlined />} style={BTN_ACTION(COULEURS_ACTION.danger)}>
              Supprimer
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>Bannières App</Breadcrumb.Item>
        </Breadcrumb>

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 16,
        }}>
          <div>
            <h3 style={{ margin: 0 }}>Bannières App Mobile</h3>
            <div style={{ color: '#8c8c8c', fontSize: 13 }}>
              {emplacement === 'accueil'
                ? "Affichées sur l'écran d'accueil après les services. Triées par ordre croissant."
                : "Affichées en haut de l'onglet Boutique. Peuvent ouvrir une boutique ou un rayon au clic."}
            </div>
          </div>
          <Space align="end" size={12}>
            <div>
              <div className="libelle-emplacement">Emplacement dans l'app</div>
              <Segmented
                className="segmented-emplacement"
                size="large"
                value={emplacement}
                onChange={setEmplacement}
                options={[
                  {
                    value: 'accueil',
                    icon: <HomeOutlined />,
                    label: (
                      <span>
                        Accueil
                        <span className="compteur-onglet">{nbAccueil}</span>
                      </span>
                    ),
                  },
                  {
                    value: 'boutique',
                    icon: <ShopOutlined />,
                    label: (
                      <span>
                        Boutique
                        <span className="compteur-onglet">{nbBoutique}</span>
                      </span>
                    ),
                  },
                ]}
              />
            </div>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}
              style={{ height: 42 }}>
              Nouvelle bannière
            </Button>
          </Space>
        </div>

        <Table
          className="moto-table"
          rowKey="_id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={banners.filter(b => emplacementDe(b) === emplacement)}
          locale={{
            ...LOCALE_TABLE,
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span style={{ color: '#8c8c8c' }}>
                    {emplacement === 'boutique'
                      ? "Aucune bannière pour l'onglet Boutique. Créez-en une avec « Nouvelle bannière » : elle s'affichera en haut de la Boutique et pourra ouvrir une boutique ou un rayon au clic."
                      : "Aucune bannière d'accueil pour le moment."}
                  </span>
                }
              />
            ),
          }}
          pagination={PAGINATION_TABLE('bannière')}
          scroll={{ x: 'max-content' }}
        />
      </div>

      <Modal
        title={editing ? "Modifier la bannière" : "Nouvelle bannière"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText={editing ? "Enregistrer" : "Créer"}
        cancelText="Annuler"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item label={editing ? "Remplacer l'image (optionnel)" : "Image *"}>
            <Upload
              accept="image/*"
              listType="picture-card"
              fileList={fileList}
              beforeUpload={() => false}
              onChange={({ fileList }) => setFileList(fileList.slice(-1))}
              onRemove={() => setFileList([])}
            >
              {fileList.length === 0 && (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 6 }}>Upload</div>
                </div>
              )}
            </Upload>
            {editing && editing.imageUrl && fileList.length === 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}>Image actuelle :</div>
                <Image src={editing.imageUrl} width={180} style={{ borderRadius: 6 }} />
              </div>
            )}
          </Form.Item>
          <Form.Item name="titre" label="Titre (optionnel)">
            <Input placeholder="Ex : Soldes de Noël -30%" maxLength={80} />
          </Form.Item>
          <Form.Item name="description" label="Description (optionnel)">
            <Input.TextArea rows={2} maxLength={160} placeholder="Courte phrase affichée sous le titre" />
          </Form.Item>
          <Form.Item
            name="emplacement"
            label="Emplacement"
            initialValue="accueil"
            extra="Accueil = écran principal de l'app · Boutique = onglet Boutique"
          >
            <Select
              options={[
                { value: 'accueil', label: "Écran d'accueil" },
                { value: 'boutique', label: 'Onglet Boutique' },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="cibleType"
            label="Action au clic"
            initialValue="aucune"
            extra="Une bannière décorative n'est pas cliquable."
          >
            <Select
              options={[
                { value: 'aucune', label: 'Aucune (décorative)' },
                { value: 'commerce', label: 'Ouvrir une boutique' },
                { value: 'categorie', label: 'Ouvrir un rayon (catégorie)' },
              ]}
            />
          </Form.Item>

          {cibleType === 'commerce' && (
            <Form.Item
              name="cibleId"
              label="Boutique ciblée"
              rules={[{ required: true, message: 'Choisissez la boutique à ouvrir' }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Choisir une boutique"
                options={commerces.map(c => ({ value: c._id, label: c.nom }))}
              />
            </Form.Item>
          )}
          {cibleType === 'categorie' && (
            <Form.Item
              name="cibleId"
              label="Rayon ciblé"
              rules={[{ required: true, message: 'Choisissez le rayon à ouvrir' }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Choisir une catégorie"
                options={categories.map(c => ({ value: c._id, label: c.nom }))}
              />
            </Form.Item>
          )}

          <Form.Item name="ordre" label="Ordre d'affichage" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren={<EyeOutlined />} unCheckedChildren={<EyeInvisibleOutlined />} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
